import { motion } from 'framer-motion';
import {
  BookMarked,
  BookOpen,
  Hexagon,
  LogOut,
  Menu,
  Settings,
  User,
  Users,
  X,
} from 'lucide-react';
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMode } from '../contexts/ModeContext';
import ModeToggle from './ModeToggle';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { mode } = useMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: BookOpen },
    { name: 'Wiki', href: '/wiki', icon: BookMarked },
    { name: 'Profile', href: '/profile', icon: User },
  ];

  const adminNavigation = [
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Version Manager', href: '/admin/exercises', icon: Settings },
    { name: 'Sync Dashboard', href: '/admin/sync', icon: Settings },
  ];

  const isAdminPage = location.pathname.startsWith('/admin');

  // Redirect if mode doesn't match current page (but allow certain pages in both modes)
  React.useEffect(() => {
    if (!user) return;

    // Pages accessible in both modes
    const sharedPages = [
      '/profile',
      '/journal',
      '/wiki',
      '/',
      '/login',
      '/auth/callback',
      '/reset-password',
    ];
    if (sharedPages.includes(location.pathname)) return;

    if (mode === 'student' && isAdminPage) {
      navigate('/dashboard');
    } else if (mode === 'admin' && !isAdminPage) {
      navigate('/admin/exercises');
    }
  }, [mode, isAdminPage, user, navigate, location.pathname]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-50 to-slate-100'>
      {/* Header */}
      <header className='glass-effect sticky top-0 z-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            {/* Logo */}
            <Link to='/' className='flex items-center space-x-2'>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                className='w-8 h-8'
              >
                <Hexagon className='w-8 h-8 text-hexagon' />
              </motion.div>
              <span className='text-xl font-bold gradient-text'>LuxLabz</span>
            </Link>

            {/* Desktop Navigation */}
            {user && (
              <nav className='hidden md:flex items-center space-x-6'>
                {mode === 'student' ? (
                  <>
                    {navigation.map(item => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-hexagon text-white shadow-lg'
                              : 'text-gray-600 hover:text-hexagon hover:bg-white/50'
                          }`}
                        >
                          <Icon className='w-5 h-5' />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </>
                ) : (
                  <>
                    {adminNavigation.map(item => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-hexagon text-white shadow-lg'
                              : 'text-gray-600 hover:text-hexagon hover:bg-white/50'
                          }`}
                        >
                          <Icon className='w-5 h-5' />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </>
                )}

                {/* Mode Toggle */}
                <ModeToggle />

                <button
                  onClick={handleSignOut}
                  className='flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200'
                >
                  <LogOut className='w-5 h-5' />
                  <span>Sign Out</span>
                </button>
              </nav>
            )}

            {/* Mobile menu button */}
            {user && (
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className='md:hidden p-2 rounded-lg text-gray-600 hover:text-hexagon hover:bg-white/50 transition-all duration-200'
              >
                {mobileMenuOpen ? (
                  <X className='w-6 h-6' />
                ) : (
                  <Menu className='w-6 h-6' />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && user && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className='md:hidden glass-effect border-t border-white/20'
          >
            <div className='px-4 py-6 space-y-4'>
              {mode === 'student' ? (
                <>
                  {navigation.map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-hexagon text-white shadow-lg'
                            : 'text-gray-600 hover:text-hexagon hover:bg-white/50'
                        }`}
                      >
                        <Icon className='w-5 h-5' />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </>
              ) : (
                <>
                  {adminNavigation.map(item => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-hexagon text-white shadow-lg'
                            : 'text-gray-600 hover:text-hexagon hover:bg-white/50'
                        }`}
                      >
                        <Icon className='w-5 h-5' />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </>
              )}

              {/* Mode Toggle */}
              <div className='px-4 py-3'>
                <ModeToggle />
              </div>

              <button
                onClick={() => {
                  handleSignOut();
                  setMobileMenuOpen(false);
                }}
                className='flex items-center space-x-3 w-full px-4 py-3 text-left text-gray-600 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200'
              >
                <LogOut className='w-5 h-5' />
                <span>Sign Out</span>
              </button>
            </div>
          </motion.div>
        )}
      </header>

      {/* Main Content */}
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {children}
      </main>

      {/* Footer */}
      <footer className='mt-auto py-8 text-center text-gray-500'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <p>&copy; 2024 LuxLabz. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
